import { useContext, useEffect, useMemo, useState } from "react";
import { store } from "../components/Store";
import produce from "immer";
import { PersonalAccountAPI } from "../services/api/personal-account";
import _ from "lodash";
import { toast } from "react-toastify";

/**
 * Hook personalizado para gestionar las instituciones del usuario en su cuenta personal.
 *
 * @returns {Object} Objeto con las siguientes propiedades y funciones:
 * - `current` {Object|null}: Institución actual seleccionada por el usuario o `null` si no hay ninguna.
 * - `all` {Array}: Lista de todas las instituciones asociadas al usuario.
 * - `setCurrentInstitution` {Function}: Función para establecer una institución como actual.
 * - `hasAvailableBooks` {boolean}: Indica si el usuario tiene libros disponibles en su cuenta personal.
 *  - Parámetros:
 *  - `institutionName` {string}: Identificador de la institución que se quiere marcar como actual.
 * 	- `institutionIndex` {number}: Indice de la institución que se quiere marcar como actual.
 *
 *
 *
 * @description
 * Este hook obtiene las instituciones asociadas al usuario desde el estado global y permite:
 * - Consultar la institución actualmente seleccionada.
 * - Obtener la lista completa de instituciones.
 * - Cambiar la institución actual en el estado global.
 */

export const usePersonalAccountInstitutionsManager = () => {
	const { state, dispatch } = useContext(store);
	const mainDataLoaded = state.mainDataLoaded;
	const isPersonalAccount = state?.isPersonalAccount;
	const [institutionChangeIntent, setInstitutionChangeIntent] = useState({
		institutionName: null,
		institutionIndex: null,
		loading: false,
	});

	const institutions = useMemo(() => {
		return Array.isArray(state?.personalAccount?.institutions)
			? state.personalAccount.institutions
			: [];
	}, [state.personalAccount.institutions]);

	const current = useMemo(() => {
		if (institutions.length > 0) {
			return institutions.find((institution) => institution.current === true);
		}
		return null;
	}, [institutions]);

	const all = institutions;
	const hasAvailableBooks = useMemo(() => {
		if (mainDataLoaded) {
			if (!isPersonalAccount) {
				return true;
			}
			return !isPersonalAccount ? true : all?.length > 0 && current && current?.name !== null;
		}
		return true;
	}, [mainDataLoaded, isPersonalAccount, all, current]);

	const setCurrentInstitution = async ({ institutionName, institutionIndex }) => {
		setInstitutionChangeIntent({ institutionName, institutionIndex, loading: true });

		const prevInstitutions = _.cloneDeep(institutions);
		// const _institutions = produce(institutions, (draft) => {
		// 	draft.map((inst) => {
		// 		if (inst.name === institutionName) {
		// 			inst.current = true;
		// 		} else {
		// 			inst.current = false;
		// 		}
		// 	});
		// });
		return await PersonalAccountAPI.setCurrentInstitution({
			data: { institutionName: institutionName, institutionIndex },
		})
			.then(() => {
				// dispatch({
				// 	type: "CHANGE",
				// 	path: "personalAccount.institutions",
				// 	value: _institutions,
				// });
				window.location.replace("/perfil/institucion?institutionChanged=true");
				return Promise.resolve();
			})
			.catch((err) => {
				dispatch({
					type: "CHANGE",
					path: "personalAccount.institutions",
					value: prevInstitutions,
				});
				setInstitutionChangeIntent({
					institutionName: null,
					institutionIndex: null,
					loading: false,
				});
				return Promise.reject(err);
			})
			.finally(() => {});
	};

	const removeInstitution = async ({ institutionName, institutionIndex }) => {
		const prev = _.cloneDeep(institutions);
		return await PersonalAccountAPI.removeInstitution({
			data: { institutionName, institutionIndex },
		})
			.then(() => {
				const _institutions = produce(institutions, (draft) =>
					draft.filter((inst) => inst.name !== institutionName)
				);
				dispatch({
					type: "CHANGE",
					path: "personalAccount.institutions",
					value: _institutions,
				});
				return Promise.resolve();
			})
			.catch(() => {
				dispatch({
					type: "CHANGE",
					path: "personalAccount.institutions",
					value: prev,
				});
				toast.error("Error removing institution");
				return Promise.reject();
			});
	};

	return {
		current,
		all,
		setCurrentInstitution,
		removeInstitution,
		institutionChangeIntent,
		hasAvailableBooks,
	};
};
